import {HttpClient, HttpParams} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {environment} from '@environments/environment';
import {WicketTokenData} from '@tisa-group/models/wicket/wicket-token-data';
import {LOCAL_STORAGE, WINDOW} from '@ng-web-apis/common';
import {Store} from '@ngxs/store';
import {ClearTokens, FetchToken, SetToken} from '@store/auth/auth.actions';
import {SHA256} from 'crypto-js';
import {filter, Observable, take} from 'rxjs';
import {
  WICKET_CODE_VERIFIER_KEY,
  WICKET_REDIRECT_URI,
  WICKET_REFRESH_TOKEN_KEY,
  WICKET_TOKEN_DATA_KEY,
  WICKET_TOKEN_KEY,
} from '../constants/localstorage-keys';
import {Location} from '@angular/common';
import {v4 as uuidv4} from 'uuid';
import {RouterInitStatusService} from './router-init-status.service';
import {CurrentClientState} from '@store/current-client/current-client.state';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  get tokenFromStorage(): string | null {
    return this.localStorage.getItem(WICKET_TOKEN_KEY) || null;
  }

  get refreshTokenFromStorage(): string | null {
    return this.localStorage.getItem(WICKET_REFRESH_TOKEN_KEY) || null;
  }

  get tokenData(): WicketTokenData | null {
    if (!this._tokenDataFromStorage) {
      return null;
    }

    return JSON.parse(this._tokenDataFromStorage);
  }

  get wicketConfig() {
    return this.store.selectSnapshot(CurrentClientState.getData)?.config
      .external_dependencies.wicket;
  }

  private get _tokenDataFromStorage(): string | null {
    return this.localStorage.getItem(WICKET_TOKEN_DATA_KEY);
  }

  constructor(
    @Inject(WINDOW) readonly window: Window,
    @Inject(LOCAL_STORAGE) readonly localStorage: Storage,
    readonly http: HttpClient,
    readonly store: Store,
    readonly router: Router,
    readonly location: Location,
    readonly routerInitStatusService: RouterInitStatusService,
  ) {}

  login() {
    if (!this.wicketConfig) {
      return;
    }
    const code_verifier = uuidv4();
    const codeChallange = SHA256(code_verifier).toString();
    let backUrl = this.window.location.href;
    const languages = ['pl', 'en', 'lt', 'pt', 'fr'];

    if (languages?.forEach) {
      // removing languages from route, WICKET will set prefferedLanguage param based on user peferences
      languages.forEach((language: string) => {
        backUrl = backUrl.replace(`/${language}/`, '/');
      });
    }

    this.localStorage.setItem(WICKET_CODE_VERIFIER_KEY, code_verifier);
    this.localStorage.setItem(WICKET_REDIRECT_URI, backUrl);

    const params = new HttpParams()
      .set('application_id', this.wicketConfig?.application_id)
      .set('backurl', backUrl)
      .set('code_challenge', codeChallange)
      .set('code_challenge_method', 'S256');

    const loginUrlWithParams = `${
      this.wicketConfig.frontend_url
    }/login?${params.toString()}`;

    this.window.location.href = loginUrlWithParams;
  }

  logout() {
    if (!this.wicketConfig) {
      return;
    }
    const backUrl = this.window.location.href;

    const params = new HttpParams()
      .set('application_id', this.wicketConfig.application_id)
      .set('backurl', backUrl)
      .set('logout_query_param_key', 'logout');

    const loginUrlWithParams = `${
      this.wicketConfig.frontend_url
    }/signout?${params.toString()}`;

    this.window.location.href = loginUrlWithParams;
  }

  fetchToken(code: string): Observable<WicketTokenData> {
    return this.http.post<WicketTokenData>(
      `${this.wicketConfig?.url}/api/v2/identity/auth/token`,
      {
        application_id: this.wicketConfig?.application_id || '',
        code_verifier: this.localStorage.getItem(WICKET_CODE_VERIFIER_KEY),
        redirect_uri: this.localStorage.getItem(WICKET_REDIRECT_URI),
        code,
      },
    );
  }

  refreshToken(refreshToken: string): Observable<WicketTokenData> {
    return this.http.post<WicketTokenData>(
      `${this.wicketConfig?.url}/p/user/token/refresh`,
      {
        application_id: this.wicketConfig?.application_id || '',
        refresh_token: refreshToken,
      },
    );
  }

  // Helper for testing and sychronizing between tabs
  listenForLocalStorageTokenChanges(): void {
    this.window.onstorage = ({key, oldValue, newValue}: StorageEvent) => {
      if (key === WICKET_TOKEN_KEY) {
        // Logged out in another tab
        if (oldValue !== null && newValue === null) {
          this.store.dispatch(ClearTokens);

          return;
        }

        // Logged in in another tab
        if (oldValue === null && newValue !== null) {
          this.store.dispatch(
            new SetToken(this.tokenFromStorage, this.refreshTokenFromStorage),
          );

          return;
        }
      }
    };
  }

  saveTokenDataToLocalStorage(tokenData: string) {
    this.localStorage.setItem(WICKET_TOKEN_DATA_KEY, tokenData);
  }

  saveTokensToLocalStorage(token: string, refreshToken: string) {
    // Keep the order - important for synchronizing between tabs
    this.localStorage.setItem(WICKET_REFRESH_TOKEN_KEY, refreshToken);
    this.localStorage.setItem(WICKET_TOKEN_KEY, token);
  }

  fetchTokenAfterWicketLogin(): void {
    const currentUrl = this.window.location.href;
    const params = new URL(currentUrl).searchParams;
    const code = params.get('code');

    if (code) {
      this.store.dispatch(new FetchToken(code)).subscribe();
    }
  }

  logoutAfterWicketLogout(): void {
    const currentUrl = this.location.path();

    this._resetUrlAfterWicketRedirect('logout', currentUrl);

    this.store.dispatch(ClearTokens);
  }

  removeTokensFromStorage(): void {
    this.localStorage.removeItem(WICKET_TOKEN_KEY);
    this.localStorage.removeItem(WICKET_REFRESH_TOKEN_KEY);
  }

  private _resetUrlAfterWicketRedirect(
    parameterToBeRemoved: string,
    currentUrl: string,
  ): void {
    if (!currentUrl.includes(parameterToBeRemoved)) {
      return;
    }
    const params = new URLSearchParams(currentUrl.split('?')[1]);

    params.delete(parameterToBeRemoved);

    this.routerInitStatusService.routerInititlized$
      .pipe(filter(Boolean), take(1))
      .subscribe(() =>
        this.router.navigate(currentUrl.split('?')[0].split('/').filter(Boolean), {
          queryParams: Object.fromEntries(params.entries()),
        }),
      );

    this.localStorage.removeItem(WICKET_CODE_VERIFIER_KEY);
    this.localStorage.removeItem(WICKET_REDIRECT_URI);
  }

  goToUserProfile() {
    if (!this.wicketConfig) {
      return;
    }
    const params = new HttpParams()
      .set('application_id', this.wicketConfig.application_id)
      .set('backurl', encodeURIComponent(this.window.location.href))
      .set('logout_query_param_key', 'logout');

    const loginUrlWithParams = `${this.wicketConfig.frontend_url}?${params.toString()}`;

    this.window.location.href = loginUrlWithParams;
  }
}
